import React, { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import TspLoadError from "../../../../components/common/TspLoadError";

// One section of a place card (Visit Info, Book Distribution, Accommodation,
// Media, Advice, Public Response): its form and its saved records, each with
// Edit / Remove — all in place, never on another page.
//
// Opening the section's tab opens its form straight away: while the section
// has room for another record, an empty add form is shown (after a save it is
// emptied for the next one). Edit loads a record into the form instead; Cancel
// on the add form closes it, and "Add …" brings it back. A section that holds
// one record (Visit Info) shows that record, with Edit, once it is saved.
//
// The records and the actions come from the place's store (staged in the browser
// on Add Journey, saved through the API on Edit Journey): every action returns
// a promise of { ok } or { ok: false, message }; a message stays next to the
// form, and the form stays open with what was typed, so nothing is lost.
//
// renderForm({ initial, submit, cancel, busy, idPrefix }) draws the section's own
// form; `submit(values)` runs onAdd / onUpdate. renderItem(item) is a record's
// summary. `blocked` (a node) replaces the section when it cannot be used at
// all; `note` (a node) is shown under the heading as information.
export default function SectionShell({
  idPrefix,
  title,
  addLabel,
  emptyText,
  items = [],
  renderItem,
  renderForm,
  onAdd,
  onUpdate,
  onRemove,
  canAdd = true,
  canEdit = () => true,
  canRemove = () => true,
  removeLabel = (item) => `Remove this ${title.toLowerCase()} record`,
  confirmRemove = "Remove this record?",
  max = Infinity,
  loading = false,
  error = null,
  onRetry,
  blocked = null,
  note = null,
  disabled = false,
  afterRecords = null,
}) {
  // "auto" (the add form is open whenever there is room) | "closed" (add form
  // cancelled) | { kind: "edit", item }
  const [mode, setMode] = useState("auto");
  // Bumped after a record is added, so the add form starts empty again.
  const [formKey, setFormKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const hasRoom = canAdd && !blocked && items.length < max;
  // The records are known (not on their first load, not failed) — so a section
  // that already has its one record never flashes an empty add form.
  const settled = !loading && !error;
  const editing = mode && mode.kind === "edit" ? mode : mode === "auto" && hasRoom && settled ? { kind: "add" } : null;
  const editingItemId = editing && editing.kind === "edit" ? editing.item.id : null;

  const run = async (action) => {
    setBusy(true);
    setMessage("");
    try {
      const result = await action();
      if (result && result.ok === false) {
        setMessage(result.message || "Could not be saved.");
        return false;
      }
      return true;
    } finally {
      setBusy(false);
    }
  };

  // The form changes in the same update that ends "Saving…", so nothing clicked
  // right after a save is overridden by it.
  const submit = (values) =>
    run(async () => {
      const result = await (editing.kind === "edit" ? onUpdate(editing.item.id, values) : onAdd(values));
      if (!result || result.ok !== false) {
        if (editing.kind === "add") setFormKey((key) => key + 1);
        setMode("auto");
      }
      return result;
    });

  const remove = async (item) => {
    if (!window.confirm(confirmRemove)) return;
    await run(() => onRemove(item.id));
  };

  const header = (
    <div className="tsp-pc-section-head">
      <h5 className="tsp-pc-section-title" id={`${idPrefix}-title`}>
        {title}
      </h5>
      {!editing && hasRoom && settled && (
        <button type="button" className="tsp-btn tsp-btn-outline-primary tsp-btn-sm" onClick={() => { setMessage(""); setMode("auto"); }} disabled={disabled}>
          <Plus size={13} aria-hidden="true" /> {addLabel}
        </button>
      )}
    </div>
  );

  if (blocked) {
    return (
      <section className="tsp-pc-section" aria-labelledby={`${idPrefix}-title`}>
        {header}
        {blocked}
      </section>
    );
  }

  return (
    <section className="tsp-pc-section" aria-labelledby={`${idPrefix}-title`}>
      {header}
      {note && <p className="tsp-pc-note">{note}</p>}

      {error ? (
        <TspLoadError error={error} onRetry={onRetry} what={`the ${title.toLowerCase()} records`} />
      ) : loading ? (
        <p className="tsp-muted-text">Loading…</p>
      ) : items.length === 0 ? (
        !editing && <p className="tsp-muted-text tsp-pc-empty">{emptyText}</p>
      ) : (
        <ul className="tsp-pc-records" aria-label={`Saved ${title.toLowerCase()}`}>
          {items.map((item) => (
            <li key={item.id} className={`tsp-pc-record${editingItemId === item.id ? " is-editing" : ""}`}>
              <div className="tsp-pc-record-body">{renderItem(item)}</div>
              <div className="tsp-pc-record-actions">
                {onUpdate && canEdit(item) && (
                  <button type="button" className="tsp-btn tsp-btn-outline-info tsp-btn-sm" onClick={() => { setMessage(""); setMode({ kind: "edit", item }); }} disabled={disabled || busy || editingItemId !== null}>
                    <Pencil size={12} aria-hidden="true" /> Edit
                  </button>
                )}
                {onRemove && canRemove(item) && (
                  <button type="button" className="tsp-icon-link-btn tsp-row-delete-btn" onClick={() => remove(item)} disabled={disabled || busy || editingItemId !== null} aria-label={removeLabel(item)}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {afterRecords}

      {editing && (
        <div key={editing.kind === "edit" ? `edit-${editingItemId}` : `add-${formKey}`} className="tsp-pc-form" role="group" aria-label={editing.kind === "edit" ? `Edit ${title}` : addLabel}>
          {renderForm({
            initial: editing.kind === "edit" ? editing.item : null,
            submit,
            cancel: () => {
              setMode(editing.kind === "edit" ? "auto" : "closed");
              setMessage("");
            },
            busy: busy || disabled,
            idPrefix: `${idPrefix}-form`,
          })}
          {message && (
            <p className="tsp-form-error" role="alert">
              {message}
            </p>
          )}
        </div>
      )}
      {!editing && message && (
        <p className="tsp-form-error" role="alert">
          {message}
        </p>
      )}
    </section>
  );
}

// A section form's body. It is NOT a <form>: the whole builder page is one
// (Create / Update Journey), and a nested form would submit the page too. Enter
// in one of its text inputs saves this section only — never the page.
export function SectionForm({ onSubmit, children }) {
  const onKeyDown = (event) => {
    if (event.key !== "Enter") return;
    const { tagName, type } = event.target;
    if (tagName !== "INPUT" || ["file", "checkbox", "radio", "button", "submit"].includes(type)) return;
    event.preventDefault();
    onSubmit();
  };
  return (
    <div className="tsp-pc-form-body" onKeyDown={onKeyDown}>
      {children}
    </div>
  );
}

// Save / Cancel under a section form.
export function FormButtons({ busy, onSave, onCancel, saveLabel = "Save" }) {
  return (
    <div className="tsp-pc-form-actions">
      <button type="button" className="tsp-btn tsp-btn-primary tsp-btn-sm" onClick={onSave} disabled={busy}>
        {busy ? "Saving…" : saveLabel}
      </button>
      <button type="button" className="tsp-btn tsp-btn-outline tsp-btn-sm" onClick={onCancel} disabled={busy}>
        Cancel
      </button>
    </div>
  );
}

// Book distribution, public response and media are filed under the place's
// recorded visit (the server needs one). Their forms open all the same: saving
// one for a place without a visit records the visit first (on the place's visit
// date), which is said here so it is never a surprise.
export function autoVisitNote(place) {
  return `No visit recorded for ${place.city || "this place"} yet — saving here also records its visit${place.plannedDate ? " on its visit date" : ""}. Its time and location can be added in Visit Info.`;
}
